import React, { useState, useEffect, useContext } from 'react';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { useLayoutGroupProps } from '@thd-olt-component-react/layout-manager';
import { Card, Placeholder, CardTitle, CardContent } from '@thd-olt-component-react/core-ui';
import AccountSnapshot from './account-snapshot/AccountSnapshot.component';
import LinkAccounts from './link-account/LinkAccounts.component';
import Prequal from './prequal/Prequal.component';
import ApplyNow from './apply-now/ApplyNow.component';
import Error from './error/Error.component';
import ProductSpotlight from './product-spotlight/product-spotlight.component';
import SsoRedirectForm from './plccUtils/SsoRedirectForm.component';
import AccountSelectorModal from './account-selector-modal/AccountSelectorModal.component';
// import { getSsoUrl, fetchPlccData } from './mockApi'; // TODO remove when read to deploy
import {
  ssoExists,
  getDefaultCard,
  unauthorizedError,
  accountUnlinkedDueToInactivity,
  plccCardSaved,
  defaultURL,
  defaultCard401CitiError,
  isErrorResponse,
} from './plccUtils';
import {
  newRelic,
  isNotAuthenticated,
  fetchPlccData, // TODO uncomment when read to deploy
  getSsoUrl // TODO uncomment when read to deploy
} from './utils';
import './PrivateLabelCreditCard.scss';

export const PLCCPod = (props) => {
  const experienceContext = useContext(ExperienceContext);
  const { channel } = experienceContext;
  const { storeId } = useStore();

  const { paymentEstimatorVersion = 'v1.0.43' } = useLayoutGroupProps({ props });

  const [showPrequal, setShowPrequal] = useState(true);
  const [showErrorComponent, setShowErrorComponent] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userInStore, setUserInStore] = useState(false);
  const [savedConsumerButNoSso, setSavedConsumerButNoSso] = useState(false);
  const [savedCommercialButNoSso, setSavedCommercialButNoSso] = useState(false);
  const [accountSnapshotData, setAccountSnapshotData] = useState(false);
  const [getSsoUrlResponse, setGetSsoUrlResponse] = useState(false);
  const [prequalInvitationNumber, setPrequalInvitationNumber] = useState(null);
  const [showAccountSelectorModal, setShowAccountSelectorModal] = useState(
    false
  );
  const [productData, setProductData] = useState(null);

  const showRelinkAccountsUi = (defaultCard) => {
    setShowPrequal(false);
    setSavedConsumerButNoSso(defaultCard.accountType === 'PLCN_HOMEDEPOT');
    setSavedCommercialButNoSso(
      defaultCard.accountType === 'PLCR_HOMEDEPOT' || defaultCard.accountType === 'PLNP_HOMEDEPOT'
    );
    setIsLoading(false);
  };

  const showRelinkAfterInactivityUi = () => {
    setShowPrequal(false);
    setSavedConsumerButNoSso(true);
    setSavedCommercialButNoSso(true);
    setIsLoading(false);
  };

  const showSnapshotErrorUi = () => {
    setShowPrequal(false);
    setShowErrorComponent(true);
    setIsLoading(false);
  };

  const showAccountSnapshotUi = (defaultCard) => {
    setAccountSnapshotData(defaultCard);
    setShowPrequal(false);
    setIsLoading(false);
  };

  const showConsumerCardSavedUi = (data) => {
    setShowPrequal(false);
    setSavedConsumerButNoSso(true);
    setSavedCommercialButNoSso(
      plccCardSaved(data, 'PLNP_HOMEDEPOT')
      || plccCardSaved(data, 'PLCR_HOMEDEPOT')
    );
    setIsLoading(false);
  };

  const showCommercialCardSavedUi = () => {
    setShowPrequal(false);
    setSavedCommercialButNoSso(true);
    setIsLoading(false);
  };

  const showPrequalUi = (prequalInvitation) => {
    setPrequalInvitationNumber(prequalInvitation);
    setShowPrequal(true);
    setIsLoading(false);
  };

  const showProductSpotlightUi = (product) => {
    setProductData(product);
    setShowPrequal(false);
    setIsLoading(false);
  };

  const showApplyNowUi = () => {
    setShowPrequal(false);
    setIsLoading(false);
  };

  const redirectToCitiBauLogin = (cardType) => {
    window.location.assign(defaultURL[cardType]);
  };

  const redirectToThdMyAccount = () => {
    window.location.assign(`${window.location.origin}/account/view/thdcreditcard`);
  };

  const clickHandlerRedirectToCiti = async (
    processIndicator = 'dashboard',
    siteId = 'PLCN_HOMEDEPOT'
  ) => {
    setIsLoading(true);
    try {
      const response = await getSsoUrl(channel === 'mobile', siteId);
      const json = await response.json(); // TODO uncommented when ready to deploy
      newRelic.logPlccApiResponse(json);
      if (json.errors) {
        setShowErrorComponent(true);
        setIsLoading(false);
      } else if (json?.urlType === 'SSOLOGIN') {
        json.processIndicator = processIndicator;
        setGetSsoUrlResponse(json);
        setIsLoading(false);
      } else if (
        json?.urlType === 'AUTHORIZE' || json?.urlType === 'DEFAULT'
      ) {
        window.location.assign(json.url);
      } else {
        setShowErrorComponent(true);
        setIsLoading(false);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      setShowErrorComponent(true);
      setIsLoading(false);
    }
  };

  const clickHandlerLinkAccounts = () => {
    if (savedCommercialButNoSso) {
      setShowAccountSelectorModal(true);
    } else {
      clickHandlerRedirectToCiti();
    }
  };

  const hideAccountSelectorModal = () => {
    setShowAccountSelectorModal(false);
  };

  async function getPlccData() {

    // TODO uncomment when ready to deploy
    // Check if in store
    if (window?.Localizer?.status?.isUserInStore() === true) {
      setUserInStore(true);
      setIsLoading(false);
      return;
    }

    // TODO uncomment when ready to deploy
    // Check Auth Status before proceeding
    if (isNotAuthenticated()) {
      showApplyNowUi();
      return;
    }

    try {
      const apiCallStartTime = performance.now();
      const response = await fetchPlccData(channel, storeId);
      const apiCallEndTime = performance.now();
      const json = await response.json(); // TODO uncomment when ready to deploy
      const apiCallDurationInSeconds = parseFloat(
        // eslint-disable-next-line no-new-wrappers
        new String((apiCallEndTime - apiCallStartTime) / 1000).substring(0, 6)
      );
      newRelic.logPlccApiResponse(json, response.status, apiCallDurationInSeconds);
      if (ssoExists(json)) {
        const defaultCard = getDefaultCard(json.accounts);
        if (defaultCard401CitiError(defaultCard)) {
          showRelinkAccountsUi(defaultCard);
        } else if (defaultCard.errors) {
          showSnapshotErrorUi();
        } else {
          showAccountSnapshotUi(defaultCard);
        }
      } else if (unauthorizedError(json) || isErrorResponse(json)) {
        showApplyNowUi();
      } else if (accountUnlinkedDueToInactivity(json)) {
        showRelinkAfterInactivityUi();
      } else if (plccCardSaved(json, 'PLCN_HOMEDEPOT')) {
        showConsumerCardSavedUi(json);
      } else if (
        plccCardSaved(json, 'PLNP_HOMEDEPOT')
        || plccCardSaved(json, 'PLCR_HOMEDEPOT')
      ) {
        showCommercialCardSavedUi();
      } else if (json.prequalInvitation) {
        showPrequalUi(json.prequalInvitation);
      } else if (json.product) {
        showProductSpotlightUi(json.product);
      } else {
        showApplyNowUi();
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      showApplyNowUi();
    }
  }

  useEffect(() => {
    getPlccData();
  }, []);

  const plccCardContent = () => {
    if (userInStore) {
      return <ApplyNow paymentEstimatorVersion={paymentEstimatorVersion} isMobile={channel === 'mobile'} />;
    }

    if (getSsoUrlResponse) {
      return <SsoRedirectForm ssoResponse={getSsoUrlResponse} />;
    }

    if (showPrequal) {
      return (
        <Prequal
          prequalInvitationNumber={prequalInvitationNumber}
          paymentEstimatorVersion={paymentEstimatorVersion}
          showApplyNow={showApplyNowUi}
          isMobile={channel === 'mobile'}
        />
      );
    }

    if (showErrorComponent) {
      return <Error redirectToCiti={redirectToCitiBauLogin} />;
    }

    if (accountSnapshotData) {
      return (
        <AccountSnapshot
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...accountSnapshotData}
          redirectToCiti={clickHandlerRedirectToCiti}
          redirectToThdMyAccount={redirectToThdMyAccount}
        />
      );
    }

    if (savedConsumerButNoSso || savedCommercialButNoSso) {
      return (
        <>
          {showAccountSelectorModal && (
            <AccountSelectorModal
              closeModal={hideAccountSelectorModal}
              linkAccounts={clickHandlerRedirectToCiti}
              isMobile={channel === 'mobile'}
            />
          )}
          <LinkAccounts
            linkAccounts={clickHandlerLinkAccounts}
            consumerCard={savedConsumerButNoSso}
            commercialCard={savedCommercialButNoSso}
            isMobile={channel === 'mobile'}
          />
        </>
      );
    }

    if (productData) {
      return (
        <ProductSpotlight
          product={productData}
          isMobile={channel === 'mobile'}
          paymentEstimatorVersion={paymentEstimatorVersion}
        />
      );
    }

    return <ApplyNow paymentEstimatorVersion={paymentEstimatorVersion} isMobile={channel === 'mobile'} />;
  };

  if (isLoading) {
    return (
      <Card>
        <CardTitle
          title="My Home Depot Credit Card"
          className="card__title"
        />
        <CardContent>
          <div>
            <Placeholder type="rect" height="190px" width="100%" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="home-page-card">
      {plccCardContent()}
    </Card>
  );
};
